/**
 * Pure helpers for the Telegram account-linking deep link.
 *
 * `telegram-link-start` returns `https://t.me/<bot>?start=<nonce>`. That link
 * only completes on a device where Telegram can actually be opened: on a
 * desktop browser with no `tg://` protocol handler registered, t.me's landing
 * page's Start button navigates to `tg://resolve?...`, the browser refuses the
 * unknown protocol, and the nonce silently expires without ever reaching the
 * bot. These helpers let the UI offer the manual path instead — open the bot
 * in Telegram Web and send `/start <nonce>`, which the webhook consumes
 * exactly like the deep link does.
 */

/** Telegram usernames: 5-32 chars, letters/digits/underscore. */
const BOT_USERNAME_RE = /^[A-Za-z0-9_]{5,32}$/;
/** Telegram start payloads: up to 64 chars of A-Z a-z 0-9 _ - */
const START_PAYLOAD_RE = /^[A-Za-z0-9_-]{1,64}$/;

export interface TelegramStartLink {
  botUsername: string;
  nonce: string;
}

/**
 * Decompose a t.me start link into its bot username and start payload.
 *
 * Fails closed (returns null) on anything that isn't a plain t.me bot link:
 * the pieces are rendered as a link target and as text the user is told to
 * paste into a chat, so a malformed or foreign URL must not flow through.
 */
export function parseTelegramStartLink(
  url: string | null | undefined,
): TelegramStartLink | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (parsed.hostname !== "t.me") return null;

  const botUsername = parsed.pathname.replace(/^\//, "");
  if (!BOT_USERNAME_RE.test(botUsername)) return null;

  const nonce = parsed.searchParams.get("start");
  if (!nonce || !START_PAYLOAD_RE.test(nonce)) return null;

  return { botUsername, nonce };
}

/** The bot's chat in Telegram Web — reachable from any browser, no app needed. */
export function telegramWebChatUrl(botUsername: string): string {
  return `https://web.telegram.org/k/#@${botUsername}`;
}

/** The message the deep link would have sent on the user's behalf. */
export function telegramStartCommand(nonce: string): string {
  return `/start ${nonce}`;
}
